import type { ITask, ITaskInput, ITasksCounts, TTasksScope } from '@torim-app/types';
import { adminUrlBuilder } from '@app/api/utils';

export class TasksApi {
  static readonly path = 'tasks';

  private static async request<T>(subPath: string, method: string, body?: unknown, query: Record<string, string> = {}): Promise<T> {
    const url = adminUrlBuilder(query, subPath ? `${TasksApi.path}/${subPath}` : TasksApi.path);
    const response = await fetch(url, {
      method,
      headers: { 'content-type': 'application/json' },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    if (!response.ok) throw `Failed tasks request: ${subPath || method}`;
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  static list(scope: TTasksScope): Promise<ITask[]> {
    return TasksApi.request<ITask[]>('', 'get', undefined, { scope });
  }

  /** Tasks for the calendar strip of `date` (yyyy-MM-dd): mine, or these employees' (managers only). */
  static listForDay(date: string, assigneeIds?: string[]): Promise<ITask[]> {
    return TasksApi.request<ITask[]>('day', 'get', undefined, assigneeIds?.length ? { date, assigneeIds: assigneeIds.join(',') } : { date });
  }

  static counts(): Promise<ITasksCounts> {
    return TasksApi.request<ITasksCounts>('counts', 'get');
  }

  static listForCustomer(customerId: string): Promise<ITask[]> {
    return TasksApi.request<ITask[]>(`customer/${customerId}`, 'get');
  }

  /** The appointment's checklist (created from its services' templates on first fetch). */
  static listForAppointment(appointmentId: string): Promise<ITask[]> {
    return TasksApi.request<ITask[]>(`appointment/${appointmentId}`, 'get');
  }

  static get(id: string): Promise<ITask> {
    return TasksApi.request<ITask>(id, 'get');
  }

  static create(input: ITaskInput): Promise<ITask> {
    return TasksApi.request<ITask>('', 'post', input);
  }

  static update(id: string, input: ITaskInput): Promise<ITask> {
    return TasksApi.request<ITask>(id, 'put', input);
  }

  static setDone(id: string, done: boolean): Promise<ITask> {
    return TasksApi.request<ITask>(`${id}/status`, 'put', { done });
  }

  static remove(id: string): Promise<void> {
    return TasksApi.request<void>(id, 'delete');
  }
}
